import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Gamepad2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/stores/authStore';
import { useMissions } from '@/hooks/useMissions';
import { fetchWithAuth } from '@/lib/api';
import { cn } from '@/lib/utils';

// 정확 매치만 숨기는 경로 — 허브에서는 자기 자신, 그 외는 입구 페이지
const EXACT_HIDDEN = new Set(['/arcade', '/login', '/signup']);
// prefix로 숨기는 경로 — 하위 페이지 전부 숨김
const PREFIX_HIDDEN = ['/admin'];

function isHiddenPath(pathname: string): boolean {
  if (EXACT_HIDDEN.has(pathname)) return true;
  return PREFIX_HIDDEN.some(prefix =>
    pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

export function ArcadeFAB() {
  const { isAuthenticated } = useAuthStore();
  const { getCompletedMissions } = useMissions();
  const location = useLocation();
  const [battleNotif, setBattleNotif] = useState(0);

  const missionReady = isAuthenticated ? getCompletedMissions().length : 0;
  const totalBadge = missionReady + battleNotif;
  const targetPath = isAuthenticated ? '/arcade' : '/login';
  const ariaLabel = isAuthenticated
    ? `오락실 열기${totalBadge > 0 ? ` (알림 ${totalBadge})` : ''}`
    : '오락실 열기 (로그인 필요)';

  useEffect(() => {
    if (!isAuthenticated) {
      setBattleNotif(0);
      return;
    }
    let cancelled = false;
    const load = () => {
      fetchWithAuth('/api/battle/notifications')
        .then(r => r.ok ? r.json() : null)
        .then(j => { if (!cancelled && j?.data) setBattleNotif(j.data.length || 0); })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [isAuthenticated]);

  if (isHiddenPath(location.pathname)) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 18 }}
      className="fixed bottom-6 right-6 z-40 md:bottom-8 md:right-8"
    >
      <Link
        to={targetPath}
        data-testid="arcade-fab"
        aria-label={ariaLabel}
        className={cn(
          'group relative flex items-center justify-center',
          'h-14 w-14 md:h-16 md:w-16 rounded-full',
          'bg-gradient-to-br from-violet-600 via-fuchsia-500 to-cyan-500',
          'shadow-lg shadow-fuchsia-500/40',
          'ring-2 ring-fuchsia-400/30 hover:ring-fuchsia-300/60',
          'transition-all hover:scale-105 active:scale-95',
          'before:absolute before:inset-0 before:rounded-full before:bg-fuchsia-500/30 before:blur-xl before:-z-10',
        )}
      >
        <Gamepad2 className="h-6 w-6 md:h-7 md:w-7 text-white drop-shadow" />
        {totalBadge > 0 && (
          <span
            data-testid="arcade-fab-badge"
            className={cn(
              'absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5',
              'flex items-center justify-center',
              'rounded-full bg-red-500 text-white text-[11px] font-bold',
              'ring-2 ring-background',
              'animate-pulse',
            )}
          >
            {totalBadge > 99 ? '99+' : totalBadge}
          </span>
        )}
      </Link>
    </motion.div>
  );
}
